import { ANNOTATION_VALUE } from "@/constants/annotation-values"
import {
  OUTPUT_TYPE_BY_METRIC_TYPE,
  type MetricDirectoryEntry,
} from "@/metrics/registry"
import { DEFAULT_MODEL } from "@/metrics/registry/configuration/values"
import type { MetricRecord } from "@/metrics/runtime"

import {
  getMetricType,
  getMetricTypeInfo,
  METRIC_TYPE,
} from "../../metric-display"
import type {
  MetricChainStatus,
  MetricFlow,
  StagedChain,
} from "../types/metricChains"

export const STAGED_METRIC_SENTINEL = "__new_metric__"

export interface TriggerCriteria {
  model_version?: string
  trigger_criteria: string
  trigger_threshold: number | string
  output_type: string
}

export interface ChainFormData {
  ulid?: string
  organization_id: string
  trigger_metric_id: string
  followup_metric_id: string
  trigger_criteria: TriggerCriteria
  status: MetricChainStatus
  isNew: boolean
  preserveExistingCriteria?: boolean
}

export function chainUpdatePayload(ulid: string, formData: ChainFormData) {
  return {
    ulid,
    trigger_metric_id: formData.trigger_metric_id,
    followup_metric_id: formData.followup_metric_id,
    ...(formData.preserveExistingCriteria
      ? {}
      : {
          trigger_criteria: {
            output_type: formData.trigger_criteria.output_type,
            trigger_criteria: formData.trigger_criteria.trigger_criteria,
            trigger_threshold: formData.trigger_criteria.trigger_threshold,
          },
        }),
    status: formData.status,
  }
}

export interface MetricChainOption {
  id: string
  name: string
  metricName: string
  output_type: string
}

interface CreateChainFormDataInput {
  chain: MetricFlow | null
  metric: MetricRecord
  metrics: MetricChainOption[]
  isTrigger: boolean
  metricDirectoryByManager: Record<string, MetricDirectoryEntry>
}

interface UpdateTriggerCriteriaInput {
  currentCriteria: TriggerCriteria
  selectedMetric: MetricChainOption | undefined
  selectedMetricRecord: MetricRecord | undefined
}

export function metricOutputType(
  metric: MetricRecord | undefined | null,
): string | undefined {
  if (metric?.metric_manager === "manager_custom_trace") {
    return getMetricType(metric) === METRIC_TYPE.SET ? "set" : "float"
  }
  return metric?.output_type ?? undefined
}

/**
 * The backend rejects chain trigger criteria whose output type is not "float"
 * or "string"; a saved "set" trigger fails validation and the chain never runs.
 */
export function isChainTriggerOutputTypeSupported(
  outputType: string | undefined,
): boolean {
  return outputType !== "set"
}

export const UNSUPPORTED_STAGED_TRIGGER_CHAIN_ERROR =
  "Values metrics are not supported as chain triggers. Remove the staged trigger chain or switch the metric away from Values before saving."

/**
 * A trigger chain staged while the draft was Count keeps stale float criteria
 * if the draft later switches to Values; the chain-card guard never re-runs, so
 * the final metric configuration decides at save time. Disabled chains stay
 * saveable without rewriting their criteria, mirroring existing chains.
 */
export function getUnsupportedStagedTriggerChainError(
  stagedChains: StagedChain[],
  metric: MetricRecord,
): string | null {
  const finalTriggerOutputType = metricOutputType(metric)
  const hasActiveUnsupportedTrigger = stagedChains.some(
    (staged) =>
      staged.role === "trigger" &&
      staged.status === "ACTIVE" &&
      !isChainTriggerOutputTypeSupported(finalTriggerOutputType),
  )
  return hasActiveUnsupportedTrigger
    ? UNSUPPORTED_STAGED_TRIGGER_CHAIN_ERROR
    : null
}

export function buildMetricChainOptions(
  metricRecordsById: Record<string, MetricRecord> | undefined,
  currentMetricId: string | undefined,
): MetricChainOption[] {
  if (!metricRecordsById) {
    return []
  }
  return Object.values(metricRecordsById)
    .filter((metric) => metric.id !== currentMetricId)
    .map((metric) => ({
      id: metric.id,
      name: metric.metric_display_name || metric.metric_name,
      metricName: metric.metric_name,
      output_type: metricOutputType(metric) || "string",
    }))
}

export function buildStagedMetricFlows(
  stagedChains: StagedChain[],
  metric: MetricRecord,
  metricRecordsById: Record<string, MetricRecord> | undefined,
): Array<{ flow: MetricFlow; staged: StagedChain }> {
  return stagedChains.map((staged) => {
    const otherRecord = metricRecordsById?.[staged.other_metric_id]
    const currentSide = {
      id: STAGED_METRIC_SENTINEL,
      name: metric.metric_name,
      displayName: metric.metric_display_name,
    }
    const otherSide = {
      id: staged.other_metric_id,
      name: otherRecord?.metric_name,
      displayName: otherRecord?.metric_display_name,
    }
    const trigger = staged.role === "trigger" ? currentSide : otherSide
    const followup = staged.role === "trigger" ? otherSide : currentSide

    return {
      staged,
      flow: {
        ulid: staged.local_id,
        organization_id: metric.organization_id as string,
        created_at: "",
        trigger_metric_id: trigger.id,
        followup_metric_id: followup.id,
        trigger_criteria: staged.trigger_criteria,
        status: staged.status,
        trigger_metric_name: trigger.name,
        trigger_metric_display_name: trigger.displayName,
        followup_metric_name: followup.name,
        followup_metric_display_name: followup.displayName,
      },
    }
  })
}

export function createChainFormData({
  chain,
  metric,
  metrics,
  isTrigger,
  metricDirectoryByManager,
}: CreateChainFormDataInput): ChainFormData {
  const triggerOutputType = resolveTriggerOutputType({
    chain,
    metric,
    metrics,
    isTrigger,
    metricDirectoryByManager,
  })

  if (chain) {
    const savedCriteria = chain.trigger_criteria as unknown as
      | TriggerCriteria
      | undefined
    const triggerCriteria =
      savedCriteria && typeof savedCriteria === "object"
        ? { ...savedCriteria, output_type: triggerOutputType }
        : createDefaultTriggerCriteria(triggerOutputType)

    return {
      ulid: chain.ulid,
      organization_id: chain.organization_id,
      trigger_metric_id: chain.trigger_metric_id,
      followup_metric_id: chain.followup_metric_id,
      trigger_criteria: triggerCriteria,
      status: (chain.status as MetricChainStatus) || "ACTIVE",
      isNew: false,
    }
  }

  const currentMetricId = metric.id || STAGED_METRIC_SENTINEL
  return {
    organization_id: metric.organization_id as string,
    trigger_metric_id: isTrigger ? currentMetricId : "",
    followup_metric_id: isTrigger ? "" : currentMetricId,
    trigger_criteria: createDefaultTriggerCriteria(triggerOutputType),
    status: "ACTIVE",
    isNew: true,
  }
}

export function updateTriggerCriteriaForMetric({
  currentCriteria,
  selectedMetric,
  selectedMetricRecord,
}: UpdateTriggerCriteriaInput): TriggerCriteria {
  const metricTypeInfo = getMetricTypeInfo(selectedMetricRecord)
  const isCategorical =
    metricTypeInfo.metricType === "categorical" &&
    metricTypeInfo.categoryOptions.length > 0
  const currentOperatorIsValid =
    metricTypeInfo.metricType === "numerical" ||
    ["==", "!="].includes(currentCriteria.trigger_criteria)

  return {
    ...currentCriteria,
    output_type:
      metricOutputType(selectedMetricRecord) ??
      selectedMetric?.output_type ??
      currentCriteria.output_type ??
      "string",
    trigger_criteria: isCategorical
      ? "=="
      : currentOperatorIsValid
        ? currentCriteria.trigger_criteria
        : metricTypeInfo.metricType === "numerical"
          ? ">"
          : "==",
    trigger_threshold: isCategorical
      ? ""
      : metricTypeInfo.metricType === "numerical"
        ? 0
        : ANNOTATION_VALUE.YES,
  }
}

function resolveTriggerOutputType({
  chain,
  metric,
  metrics,
  isTrigger,
  metricDirectoryByManager,
}: CreateChainFormDataInput): string {
  if (!isTrigger) {
    return (
      metrics.find((candidate) => candidate.id === chain?.trigger_metric_id)
        ?.output_type ?? "string"
    )
  }
  return (
    metricOutputType(metric) ??
    metricDirectoryByManager[metric.metric_manager ?? ""]?.output_type ??
    OUTPUT_TYPE_BY_METRIC_TYPE[metric.metric_manager] ??
    "string"
  )
}

function createDefaultTriggerCriteria(outputType: string): TriggerCriteria {
  return {
    output_type: outputType,
    trigger_criteria: outputType === "float" ? ">" : "==",
    trigger_threshold: outputType === "float" ? 0 : ANNOTATION_VALUE.YES,
    model_version: DEFAULT_MODEL,
  }
}
